import { Component, OnInit, ViewChild, TemplateRef, NgZone, Renderer2 } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AppService, Channel, LINK_DOMAINS, LINK_TYPES, LinkService, Notification } from '@portals/api';
import { BaseComponent } from '@portals/core';
import { CollectibleService } from '@portals/api';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels, QrcodeComponent } from '@techiediaries/ngx-qrcode';
import { BehaviorSubject, tap } from 'rxjs';
import { environment } from '@portals/environments';
import mqtt, { MqttClient } from 'mqtt';
import { Options } from 'ngx-qrcode-styling';
import { DeviceDetectorService } from 'ngx-device-detector';
import { animate, query, sequence, stagger, state, style, transition, trigger, group } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { ThemeType } from '@portals/api';
import { customAlphabet } from 'nanoid';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RESPONSE_TYPES, SCREENS } from '@portals/utils';

interface Ads {
	id: number;
	screen_one_title: string;
	screen_one_caption: string;
	screen_two_title: string;
	screen_two_caption: string;
	screen_three_title: string;
	screen_three_caption: string;
	screen_four_title: string;
	screen_four_caption: string;
	createdAt: string;
	updatedAt: string;
	publishedAt: string;
}

@Component({
	selector: 'portals-cloud',
	templateUrl: './cloud.component.html',
	styleUrls: ['./cloud.component.scss'],
	animations: [
		trigger('expandCollapse', [
			state(
				'collapsed',
				style({
					clipPath: 'polygon(0 0, 90% 0, 100% 100%, 0 100%)',
				})
			),
			state(
				'expanded',
				style({
					clipPath: 'polygon(0 0, 70% 0, 100% 100%, 0 100%)',
				})
			),
			transition('collapsed <=> expanded', [animate('1s ease-in-out')]),
		]),
		trigger('fadeIn', [
			transition(':enter', [
				query('.title', [style({ opacity: 0 }), animate('1s', style({ opacity: 1 }))]),
				query('.image-container', [
					style({ opacity: 0 }),
					stagger('1s', [
						// Slowed down the stagger
						animate('1s', style({ opacity: 1 })),
					]),
				]),
				query('.image-caption', [style({ opacity: 0 }), animate('1s', style({ opacity: 1 }))]),
			]),
		]),
		trigger('onOffOpacity', [
			transition(':enter', [style({ opacity: 0 }), animate('2000ms ease-out', style({ opacity: 1 }))]),
			// Leaving animation
			transition(':leave', [style({ opacity: 1 }), animate('2000ms ease-out', style({ opacity: 0 }))]),
		]),
		trigger('opacityInAndOut', [
			// Entering animation
			transition('collapsed => expanded', [
				style({ opacity: 0 }), // Ensure it starts from 1 if transitioning from expanded
				animate('2000ms ease-out', style({ opacity: 1 })),
			]),
			// Expanded state animation
			transition('expanded => collapsed', [
				style({ opacity: 1 }), // Ensure it starts from 0 if transitioning from collapsed
				animate('2000ms ease-out', style({ opacity: 0 })),
			]),
		]),
		trigger('moveQRCode', [
			state(
				'center',
				style({
					top: '35%',
					left: '37.5%',
					transform: 'translate(0, 0)',
				})
			),
			state(
				'bottomRight',
				style({
					top: 'auto',
					left: 'auto',
					right: '28px',
					bottom: '48px',
					transform: 'translate(0, 0)',
				})
			),
			transition('center => bottomRight', [
				animate(
					'1s ease-in-out',
					style({
						top: 'calc(100% - 304px)', // 256px height + 48px bottom margin
						left: 'calc(100% - 284px)', // 256px width + 28px right margin
					})
				),
			]),
		]),
		trigger('slideLeftImage', [
			state('Second', style({ transform: 'translateX(0)', opacity: 1 })),
			transition('void => Second', [
				style({
					transform: 'translateX(50%)',
					opacity: 0,
				}),
				animate('2000ms ease-out'),
			]),
			transition('Second => void', [animate('500ms ease-in', style({ transform: 'translateX(50%)', opacity: 0 }))]),
		]),
		trigger('slideRightImage', [
			state('Second', style({ transform: 'translateX(0)', opacity: 1 })),
			transition('void => Second', [
				style({
					transform: 'translateX(-50%)',
					opacity: 0,
				}),
				animate('2000ms ease-out'),
			]),
			transition('Second => void', [animate('500ms ease-in', style({ transform: 'translateX(-50%)', opacity: 0 }))]),
		]),
		trigger('reportMove', [
			state(
				'initial',
				style({
					transform: '{{initialTransform}}',
					opacity: '{{initialOpacity}}',
				}),
				{ params: { initialTransform: 'translateX(0%) rotate(10deg)', initialOpacity: 1 } }
			),
			state(
				'expanded',
				style({
					transform: '{{expandedTransform}}',
					opacity: 1,
				}),
				{ params: { expandedTransform: 'translateX(0) rotate(0deg)' } }
			),
			transition('initial <=> expanded', animate('1s ease-in-out')),
		]),
		trigger('reportMoveFirst', [
			state(
				'initial',
				style({
					transform: '{{initialTransform}}',
					opacity: '{{initialOpacity}}',
				}),
				{ params: { initialTransform: 'translateX(0%) rotate(2deg)', initialOpacity: 1 } }
			),
			state(
				'expanded',
				style({
					transform: '{{expandedTransform}}',
					opacity: 1,
				}),
				{ params: { expandedTransform: 'translateX(0) rotate(0deg)' } }
			),
			transition('initial <=> expanded', animate('1s ease-in-out')),
		]),
		trigger('expandCollapseMobile', [state('collapsed', style({ height: 'fit-content' })), state('expanded', style({ height: '100%' })), transition('collapsed <=> expanded', animate('1s ease-in-out'))]),
	],
})
export class CloudComponent extends BaseComponent implements OnInit {
	protected readonly NgxQrcodeErrorCorrectionLevels = NgxQrcodeErrorCorrectionLevels;
	channel!: Channel;
	reload = false;
	uuid = customAlphabet('1234567890abcdef', 10)();
	form: FormGroup;
	url!: string;
	channelUUID!: string;
	qrCodePosition: 'center' | 'bottomRight' = 'center';
	wordCount: { counter: number; words: any[]; autorefresh: boolean } = { counter: 0, words: [], autorefresh: true };
	elementType = NgxQrcodeElementTypes.URL;
	@ViewChild('dialogTemplate', { static: true }) dialogTemplate!: TemplateRef<any>;
	theme?: ThemeType;
	notification!: Notification;
	notificationID = environment.defaultNotification as number;
	correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;
	loaded = false;
	mqttClient?: MqttClient;
    protected readonly window = window;
	linkType!: LINK_TYPES;
	private refreshTimeOut: NodeJS.Timeout | undefined;
	colors = ['#B18FCF', '#D66853', '#357266', '#A31621'];
	intervalId: any;
	isExpanded = false;
	adTransitions = ['First', 'Second', 'Third', 'Fourth'];
	pageTitles = ['Jotto makes it easy to share your thoughts, ideas and experience', 'Real-Time Feedback', 'Empowering Insights', 'Powerful Ideas'];
	pageCaptions = ['Scan a code', 'Answer a question', 'Powerful Analytics', 'Get more meaningful  feedback and deliver more  meaningful experiences.'];
	currentAdScreen: string;
	adIndex = 0;
	dialogRef: any;
	clickCount = 0;
    qrcodeConfig: Options = {
        width: window.innerWidth * 0.12,
        height: window.innerWidth * 0.12,
        type: 'svg',
        image: '/assets/images/jotto-yellow.svg',
        dotsOptions: {
            color: '#491176',
            type: 'square',
        },
        backgroundOptions: {
            color: '#fbd354',
        },
        imageOptions: {
            crossOrigin: 'anonymous',
            margin: 0,
            hideBackgroundDots: false,
        },
    };
	clickTimeout: any;
	showAd = true;
	showAd$ = new BehaviorSubject<boolean>(true);
	autoRefresh$ = new BehaviorSubject<boolean>(true);
	autoRefresh = true;
	animationState = 'initial';

	constructor(private route: ActivatedRoute, private app: AppService, private formBuilder: FormBuilder, private collectibleService: CollectibleService, private linkService: LinkService, public deviceService: DeviceDetectorService, public dialog: MatDialog, public ngZone: NgZone, private renderer: Renderer2) {
		super();
		this.form = this.formBuilder.group({
			first_name: [`web-user`],
			last_name: [this.uuid],
			username: [this.uuid],
			password: [this.uuid],
			confirmed: [true],
			blocked: [false],
			role: [1, Validators.required],
			web: [true],
		});
		this.currentAdScreen = this.adTransitions[this.adIndex];
		this.showAd$.subscribe((switchState) => {
			this.onSwitchStateChange(switchState);
		});
		this.autoRefresh$.subscribe((switchState) => {
			this.onAutoRefreshChange(switchState);
		});
	}

	onAutoRefreshChange(switchState: boolean) {
		this.ngZone.run(() => {
			console.log(`Updating autorefresh ${this.autoRefresh}`);
			this.autoRefresh = switchState;
			this.wordCount = {
				...this.wordCount,
				autorefresh: switchState,
			};
			console.log(`Updating autorefresh next ${this.autoRefresh}`);
		});
	}

	onSwitchStateChange(switchState: boolean) {
		this.showAd = switchState;
		if (!switchState) {
			this.isExpanded = false;
		}
	}

	observeTopic(topic: string) {
		if (this.mqttClient) {
			this.mqttClient.subscribe(topic, (err) => {
				if (err) {
					console.error('Error subscribing to MQTT topic:', err);
				} else {
					console.log('Subscribed to MQTT topic:', topic);
				}
			});
		}
	}

    getVariedConfig(width: number, height: number): Options {
        return {
            ...this.qrcodeConfig,
            width: width,
            height: height,
        };
    }

	updateAds(ads: Ads) {
		const titles = [ads.screen_one_title, ads.screen_two_title, ads.screen_three_title, ads.screen_four_title];

		const captions = [ads.screen_one_caption, ads.screen_two_caption, ads.screen_three_caption, ads.screen_four_caption];

		titles.forEach((title, index) => {
			if (title !== undefined) {
				this.pageTitles[index] = title;
			}
		});

		captions.forEach((caption, index) => {
			if (caption !== undefined) {
				this.pageCaptions[index] = caption;
			}
		});
	}

	ngOnInit() {
		this.route.params.subscribe((params) => {
			const url = window.location.href;
			const preparedUrl = url.startsWith('http') ? url : `https://${url}`;
			const hostname = ('https://' + new URL(preparedUrl).hostname + '/') as LINK_DOMAINS;
			this.linkService
				.getLink(hostname.replace(/www\./g, ""), params['uuid'])
				.pipe(
					tap((data) => {
						if (data.ads) {
							this.updateAds(data.ads);
						}
						if (data.theme) {
							this.theme = data.theme;
						}
						this.linkType = data.type;
						this.notification = data.entity as Notification;
						this.channelUUID = this.notification.channel?.uuid as string;
						this.notificationID = this.notification.id;
						this.channel = this.notification.channel as Channel;
						this.url = this.notification.alt_stv_url as string;
						this.loaded = true;
                        this.qrcodeConfig.data = this.url;

						window.addEventListener('resize', () => {
							this.updateQrCodeSize();
						});

						this.fetchData();

						try {
							// Establish MQTT connection
							this.mqttClient = mqtt.connect('wss://' + 'message.jotto.me');

							// Handle connection success
							this.mqttClient.on('connect', () => {
								// Subscribe to a topic after successful connection
								this.observeTopic(`/clouds/${this.notificationID}`);
							});

							// Handle incoming messages
							this.mqttClient.on('message', (topic, message) => {
								console.log('message', message);
								const data = JSON.parse(message.toString());
								const words = data.words.slice(0, 50);
								// Update the word cloud
								// if the word is already in the word cloud, increment the count else add the word to the word cloud
								const updatedWordCount = [...this.wordCount.words];

								// Update the word cloud
								words.forEach((word: string) => {
									const index = updatedWordCount.findIndex((item) => item.name === word);
									if (index >= 0) {
										// Increment count if word exists
										updatedWordCount[index].count++;
									} else {
										// Add new word to the word cloud
										updatedWordCount.push({ id: updatedWordCount.length + 1, name: word, count: 1 });
									}
								});

								// Assign the updated array back to wordCount to trigger change detection
								this.wordCount = { counter: this.wordCount.counter++, words: updatedWordCount, autorefresh: this.autoRefresh };
							});
							this.mqttClient.on('error', (err) => {
								console.error('Error connecting to MQTT:', err);
							});
							this.mqttClient.on('close', () => {
								console.log('MQTT connection closed');
							});
						} catch (err) {
							console.error('Error connecting to MQTT:', err);
						}
					})
				)
				.subscribe();
		});
		this.updateQRCodePosition();
	}

	handleClick(): void {
		this.clickCount++;
		if (this.clickCount === 5) {
			this.openDialog();
			this.resetClickCount();
		} else {
			this.resetClickTimeout();
		}
		// this.openDialog();
	}

	resetClickCount(): void {
		this.clickCount = 0;
		if (this.clickTimeout) {
			clearTimeout(this.clickTimeout);
			this.clickTimeout = null;
		}
	}

	resetClickTimeout(): void {
		if (this.clickTimeout) {
			clearTimeout(this.clickTimeout);
		}
		this.clickTimeout = setTimeout(() => {
			this.resetClickCount();
		}, 1000); // Reset count after 1 second of inactivity
	}

	openDialog(): void {
		// if (this.dialogTemplate) {
		this.dialog.open(this.dialogTemplate);
		// }
	}

	closeDialog() {
		this.dialog.closeAll();
	}

	cycleScreen(): void {
		this.intervalId = setInterval(() => {
			this.adIndex = (this.adIndex + 1) % this.adTransitions.length;
			this.currentAdScreen = this.adTransitions[this.adIndex];
			if (this.adIndex === 3) {
				setTimeout(() => {
					this.animationState = 'expanded';
					setTimeout(() => {
						this.animationState = 'initial';
					}, 1000);
				}, 500);
			}
			this.updateQRCodePosition();
		}, 3000);
	}

	override ngOnDestroy(): void {
		this.mqttClient?.end();
	}

	fetchData() {
		this.collectibleService
			.momentQuery(this.notificationID)
			.pipe(
				tap((data) => {
					this.wordCount.words = data.slice(0, 50);
				})
			)
			.subscribe();
	}

	updateQrCodeSize() {
		if (this.refreshTimeOut) {
			clearInterval(this.refreshTimeOut);
			this.refreshTimeOut = setTimeout(() => {
				location.reload();
			}, 500);
		} else {
			this.refreshTimeOut = setTimeout(() => {
				location.reload();
			}, 500);
		}
	}

	navigateToUrl() {
		this.subscribe(
			this.app.registerUser(this.form.value, this.channel).pipe(
				tap(() => {
					window.open(this.url, '_self');
				})
			)
		);
	}

	toggle(event: boolean) {
		if (this.showAd) {
			this.isExpanded = event;
			if (event) {
				this.cycleScreen();
				// this.updateQRCodePosition();
			} else {
				clearInterval(this.intervalId);
				this.adIndex = 0;
				this.currentAdScreen = this.adTransitions[0];
			}
		}
	}

	getGridTemplateColumns() {
		return this.isExpanded ? '100% 0%' : '43% 1fr';
	}

	getBackgroundColorClass() {
		if (this.theme && this.theme.background_color) {
			// Map the background_color to a Tailwind class
			return { background: this.theme.background_color };
		} else {
			// bg-[#491176]
			return { background: '#491176' };
		}
	}

	private updateQRCodePosition() {
		if (this.currentAdScreen !== 'First' && this.qrCodePosition === 'center') {
			// Small delay to ensure DOM update before animation
			setTimeout(() => {
				this.qrCodePosition = 'bottomRight';
			}, 50);
		} else if (this.currentAdScreen === 'First') {
			this.qrCodePosition = 'center';
		}
	}
}
